import { getUrlSync, ICoreContext, ITelemetry } from '@msdyn365-commerce/core';
import { CartsDataActions } from '@msdyn365-commerce/retail-proxy';
import { QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
import { reaction } from 'mobx';
import { useCallback, useEffect, useRef, useState } from 'react';

import { IExtCartIconData } from '../ext-cart-icon.data';
import {
    findUnmatchedCartLines,
    ICartSnapshotLine,
    mapCartLinesToSnapshot,
    parseCartSnapshotCookie,
    writeCartSnapshotCookie
} from '../utils/cart-snapshot';

export interface IUseCartChangeDetectionOptions {
    context: ICoreContext;
    telemetry: ITelemetry;
    data: IExtCartIconData;
    cartUpdateToggle?: boolean;
    displayCartUpdate?: boolean;
    removeUserCartItems?: boolean;
}

export interface ICartChangeDetection {
    isPopupOpen: boolean;
    closePopup(): void;
    goToCart(): void;
    removeDetectedLines(): void;
}

/**
 * Detects cart lines that were merged into an authenticated user's cart from a
 * previous session by comparing the current cart against the 'onlyCart' cookie
 * snapshot, and keeps that snapshot up to date as the cart changes.
 */
export const useCartChangeDetection = (options: IUseCartChangeDetectionOptions): ICartChangeDetection => {
    const { context, data, telemetry, cartUpdateToggle = false, displayCartUpdate = false, removeUserCartItems = false } = options;
    const { isAuthenticated } = context.request.user;

    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const detectedLinesRef = useRef<ICartSnapshotLine[]>([]);
    const hasComparedAuthenticatedCartRef = useRef(false);
    const isRemovalUnderwayRef = useRef(false);

    const removeDetectedLines = useCallback((): void => {
        if (isRemovalUnderwayRef.current) {
            return;
        }
        setIsPopupOpen(false);

        const cartId = data.cart.result?.cart.Id;
        const cartLineIds = detectedLinesRef.current
            .map(line => line.id)
            .filter((lineId): lineId is string => typeof lineId === 'string' && lineId !== '');
        if (!cartId || cartLineIds.length === 0) {
            detectedLinesRef.current = [];
            return;
        }

        isRemovalUnderwayRef.current = true;
        CartsDataActions.removeCartLinesAsync(
            {
                callerContext: context.actionContext,
                queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context.actionContext)
            },
            cartId,
            cartLineIds
        )
            .then(() => {
                telemetry.warning('_cart-update Cart change popup removing cart lines');
                window.location.reload();
            })
            .catch((error: Error) => {
                telemetry.exception(error);
                detectedLinesRef.current = [];
                isRemovalUnderwayRef.current = false;
            });
    }, [context.actionContext, data.cart, telemetry]);

    const goToCart = useCallback((): void => {
        telemetry.warning('_cart-update Cart change popup redirecting to cart');
        window.location.href = getUrlSync('cart', context.actionContext) ?? '';
    }, [context.actionContext, telemetry]);

    const closePopup = useCallback((): void => {
        setIsPopupOpen(false);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined' || !cartUpdateToggle) {
            return undefined;
        }

        const processCart = (): void => {
            const cartState = data.cart.result;
            if (data.cart.status !== 'SUCCESS' || !cartState) {
                return;
            }
            const currentSnapshot = mapCartLinesToSnapshot(cartState.cart.CartLines);

            if (!isAuthenticated) {
                // Returning to anonymous resets the comparison guards.
                hasComparedAuthenticatedCartRef.current = false;
                detectedLinesRef.current = [];
                writeCartSnapshotCookie(currentSnapshot, context.request);
                return;
            }

            if (!hasComparedAuthenticatedCartRef.current) {
                hasComparedAuthenticatedCartRef.current = true;
                const previousSnapshot = parseCartSnapshotCookie();
                const unmatchedLines = findUnmatchedCartLines(previousSnapshot, currentSnapshot);
                writeCartSnapshotCookie(currentSnapshot, context.request);
                if (unmatchedLines.length > 0) {
                    detectedLinesRef.current = unmatchedLines;
                    telemetry.warning('_cart-update Cart change popup displayed');
                    if (displayCartUpdate) {
                        setIsPopupOpen(true);
                    } else if (removeUserCartItems) {
                        removeDetectedLines();
                    }
                }
                return;
            }

            writeCartSnapshotCookie(currentSnapshot, context.request);
        };

        const disposeReaction = reaction(() => ({ status: data.cart.status, cartLines: data.cart.result?.cart.CartLines }), processCart, {
            fireImmediately: true
        });
        return disposeReaction;
    }, [
        cartUpdateToggle,
        displayCartUpdate,
        removeUserCartItems,
        isAuthenticated,
        context.request,
        data.cart,
        removeDetectedLines,
        telemetry
    ]);

    return { isPopupOpen, closePopup, goToCart, removeDetectedLines };
};
